'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

const partnerNavGroups = [
  {
    label: 'Utama',
    items: [
      { label: 'Dashboard', href: '/partner/dashboard', icon: 'HomeIcon', badge: null },
      { label: 'Kavling', href: '/partner/kavling', icon: 'MapIcon', badge: null },
      { label: 'Share Center', href: '/partner/share-center', icon: 'ShareIcon', badge: null },
    ],
  },
  {
    label: 'CRM',
    items: [
      { label: 'Leads', href: '/partner/leads', icon: 'UserGroupIcon', badge: '3' },
      { label: 'Follow Up', href: '/partner/follow-up', icon: 'PhoneIcon', badge: null },
      { label: 'Booking', href: '/partner/booking', icon: 'DocumentCheckIcon', badge: null },
    ],
  },
  {
    label: 'Reward',
    items: [
      { label: 'Komisi', href: '/partner/komisi', icon: 'BanknotesIcon', badge: null },
      { label: 'Point & Reward', href: '/partner/reward', icon: 'GiftIcon', badge: null },
      { label: 'Leaderboard', href: '/partner/leaderboard', icon: 'TrophyIcon', badge: null },
    ],
  },
  {
    label: 'Tools',
    items: [
      { label: 'Performa Referral', href: '/partner/referral-performance', icon: 'ChartBarIcon', badge: null },
      { label: 'Marketing Kit', href: '/partner/marketing-kit', icon: 'PhotoIcon', badge: null },
      { label: 'Profil', href: '/partner/profil', icon: 'UserCircleIcon', badge: null },
    ],
  },
];

// Primary 4 items always visible in bottom bar
const mobileNavPrimary = [
  { label: 'Dashboard', href: '/partner/dashboard', icon: 'HomeIcon' },
  { label: 'Leads', href: '/partner/leads', icon: 'UserGroupIcon' },
  { label: 'Kavling', href: '/partner/kavling', icon: 'MapIcon' },
  { label: 'Komisi', href: '/partner/komisi', icon: 'BanknotesIcon' },
];

// All items shown in "Lainnya" drawer
const mobileNavMore = [
  { label: 'Performa Referral', href: '/partner/referral-performance', icon: 'ChartBarIcon', badge: null },
  { label: 'Share Center', href: '/partner/share-center', icon: 'ShareIcon', badge: null },
  { label: 'Follow Up', href: '/partner/follow-up', icon: 'PhoneIcon', badge: null },
  { label: 'Booking', href: '/partner/booking', icon: 'DocumentCheckIcon', badge: null },
  { label: 'Point & Reward', href: '/partner/reward', icon: 'GiftIcon', badge: null },
  { label: 'Leaderboard', href: '/partner/leaderboard', icon: 'TrophyIcon', badge: null },
  { label: 'Marketing Kit', href: '/partner/marketing-kit', icon: 'PhotoIcon', badge: null },
  { label: 'Profil', href: '/partner/profil', icon: 'UserCircleIcon', badge: null },
];

interface PartnerLayoutProps {
  children: React.ReactNode;
  title: string;
  subtitle?: string;
}

export default function PartnerLayout({ children, title, subtitle }: PartnerLayoutProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const pathname = usePathname();

  // Check if current path is one of the "more" items
  const isMoreActive = mobileNavMore.some(
    (item) => pathname === item.href || pathname.startsWith(item.href + '/')
  );

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Sidebar */}
      <aside
        className={`hidden lg:flex flex-col bg-card border-r border-border shadow-sidebar sidebar-transition flex-shrink-0 ${
          collapsed ? 'w-16' : 'w-60'
        }`}
      >
        <div className={`h-16 flex items-center border-b border-border flex-shrink-0 ${collapsed ? 'justify-center px-0' : 'px-4 gap-2'}`}>
          <AppLogo size={32} />
          {!collapsed && (
            <div>
              <span className="font-bold text-sm text-primary block">Betaland<span className="text-accent">.ID</span></span>
              <span className="text-xs text-muted-foreground">Partner Platform</span>
            </div>
          )}
        </div>

        <nav className="flex-1 py-4 flex flex-col gap-4 px-2 overflow-y-auto scrollbar-hide">
          {partnerNavGroups.map((group) => (
            <div key={group.label}>
              {!collapsed && (
                <p className="px-2 mb-1 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  {group.label}
                </p>
              )}
              <div className="flex flex-col gap-0.5">
                {group.items.map((item) => {
                  const isActive = pathname === item.href || pathname.startsWith(item.href + '/');
                  return (
                    <Link
                      key={`partner-nav-${item.href}`}
                      href={item.href}
                      title={collapsed ? item.label : undefined}
                      className={`flex items-center gap-3 px-2 py-2.5 rounded-lg transition-colors duration-150 relative group ${
                        isActive
                          ? 'bg-primary/10 text-primary font-semibold' :'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                    >
                      <Icon name={item.icon as any} size={20} className="flex-shrink-0" />
                      {!collapsed && <span className="text-sm flex-1">{item.label}</span>}
                      {!collapsed && item.badge && (
                        <span className="bg-orange text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                          {item.badge}
                        </span>
                      )}
                      {collapsed && item.badge && (
                        <span className="absolute top-1 right-1 w-2 h-2 bg-orange rounded-full" />
                      )}
                      {collapsed && (
                        <span className="absolute left-full ml-2 px-2 py-1 bg-foreground text-background text-xs rounded-md opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity">
                          {item.label}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="border-t border-border p-2">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className={`w-full flex items-center gap-3 px-2 py-2.5 rounded-lg text-muted-foreground hover:bg-muted transition-colors ${collapsed ? 'justify-center' : ''}`}
          >
            <Icon name={collapsed ? 'ChevronRightIcon' : 'ChevronLeftIcon'} size={18} />
            {!collapsed && <span className="text-sm">Ciutkan</span>}
          </button>
          <div className={`flex items-center gap-3 px-2 py-2 mt-1 rounded-lg bg-muted ${collapsed ? 'justify-center' : ''}`}>
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-bold text-primary">RA</span>
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground truncate">Rizky Andika</p>
                <p className="text-xs text-muted-foreground">Agent Senior</p>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="h-16 bg-card border-b border-border flex items-center justify-between px-4 lg:px-6 flex-shrink-0">
          <div>
            <h1 className="text-lg font-bold text-foreground">{title}</h1>
            {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-2">
            <a
              href="https://wa.me/6281234567890"
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground"
              aria-label="WhatsApp Support"
            >
              <Icon name="ChatBubbleLeftRightIcon" size={20} />
            </a>
            <button className="relative p-2 rounded-lg hover:bg-muted transition-colors" aria-label="Notifikasi">
              <Icon name="BellIcon" size={20} className="text-muted-foreground" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-orange rounded-full" />
            </button>
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-muted rounded-lg">
              <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                <span className="text-xs font-bold text-primary">RA</span>
              </div>
              <span className="text-sm font-medium">Rizky Andika</span>
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto pb-20 lg:pb-0">
          {children}
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-card border-t border-border">
        <div className="flex items-center justify-around px-1 py-1">
          {mobileNavPrimary.map((item) => {
            const isActive = pathname === item.href || pathname.startsWith(item.href + '/');
            return (
              <Link
                key={`partner-mobile-${item.href}`}
                href={item.href}
                className={`flex flex-col items-center gap-0.5 px-2 py-2 rounded-xl transition-colors flex-1 ${
                  isActive ? 'text-primary' : 'text-muted-foreground'
                }`}
              >
                <Icon name={item.icon as any} size={22} />
                <span className="text-xs font-medium leading-tight">{item.label}</span>
              </Link>
            );
          })}
          {/* Lainnya button */}
          <button
            onClick={() => setMoreOpen(true)}
            className={`flex flex-col items-center gap-0.5 px-2 py-2 rounded-xl transition-colors flex-1 ${
              isMoreActive ? 'text-primary' : 'text-muted-foreground'
            }`}
          >
            <Icon name="EllipsisHorizontalIcon" size={22} />
            <span className="text-xs font-medium leading-tight">Lainnya</span>
          </button>
        </div>
      </nav>

      {/* More menu drawer overlay */}
      {moreOpen && (
        <div className="lg:hidden fixed inset-0 z-[60] flex flex-col justify-end">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMoreOpen(false)}
          />
          {/* Drawer */}
          <div className="relative bg-card rounded-t-2xl shadow-xl pb-safe">
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-border" />
            </div>
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-border">
              <span className="text-base font-semibold text-foreground">Menu Lainnya</span>
              <button
                onClick={() => setMoreOpen(false)}
                className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground"
              >
                <Icon name="XMarkIcon" size={20} />
              </button>
            </div>
            {/* Menu grid */}
            <div className="grid grid-cols-4 gap-2 px-4 py-4">
              {mobileNavMore.map((item) => {
                const isActive = pathname === item.href || pathname.startsWith(item.href + '/');
                return (
                  <Link
                    key={`partner-more-${item.href}`}
                    href={item.href}
                    onClick={() => setMoreOpen(false)}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-xl transition-colors ${
                      isActive
                        ? 'bg-primary/10 text-primary' :'bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    <Icon name={item.icon as any} size={24} />
                    <span className="text-xs font-medium text-center leading-tight">{item.label}</span>
                    {item.badge && (
                      <span className="bg-orange text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center leading-none">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
            {/* Safe area spacer */}
            <div className="h-4" />
          </div>
        </div>
      )}
    </div>
  );
}
